/**
 * Thin adapter layer over the generated `I18NLanguages` / `I18NImport` / `I18NExport` clients.
 *
 * Provides:
 * - Typed wrappers that unwrap `AxiosResponse.data`
 * - A Blob download helper for the exported XLSX language file
 * - A manual multipart upload helper for the language-file import
 */

import type { I18NImportLogDTO, I18NLanguageStatusDTO, Language } from "@/api/data-contracts";
import { I18NExport } from "@/api/I18NExport";
import { I18NImport } from "@/api/I18NImport";
import { I18NLanguages } from "@/api/I18NLanguages";
import { ApiConfig } from "@/api/http-client";

const apiConfig: ApiConfig = {
    baseURL: "/",
    withXSRFToken: true
};

const languagesClient = new I18NLanguages(apiConfig);
const importClient = new I18NImport(apiConfig);
const exportClient = new I18NExport(apiConfig);

/**
 * Fetches the maintenance status, current entry count, and most recent import-protocol
 * entry for every language.
 */
export async function getLanguageStatuses(): Promise<I18NLanguageStatusDTO[]> {
    const response = await languagesClient.getLanguageStatuses();
    return response.data;
}

/**
 * Fetches the last 100 i18n import-protocol entries, most recent first.
 */
export async function getImportLog(): Promise<I18NImportLogDTO[]> {
    const response = await importClient.getImportLog1();
    return response.data;
}

/**
 * Fetches a single import-protocol entry with all its structured findings.
 */
export async function getImportLogDetail(id: number): Promise<I18NImportLogDTO> {
    const response = await importClient.getImportLogDetail(id);
    return response.data;
}

/**
 * Downloads the current i18n entries for the given language as an XLSX workbook Blob.
 * The caller is responsible for triggering the browser download (see `lib/blob-download.ts`).
 */
export async function exportLanguage(language: Language): Promise<Blob> {
    // Passing `format: "blob"` sets Axios `responseType: "blob"` so the response body is
    // delivered as a Blob at runtime. The generated client declares `request<string, ...>`,
    // so `response.data` is typed as `string` even though Axios returns a `Blob` when
    // `responseType: "blob"` is active. The double cast `as unknown as Blob` bridges that
    // generated-type / runtime-type mismatch — it is safe as long as the backend sends a
    // binary response and `format: "blob"` is present.
    const response = await exportClient.exportLanguage(language, {
        format: "blob"
    } as Parameters<typeof exportClient.exportLanguage>[1]);
    return response.data as unknown as Blob;
}

/**
 * Uploads an XLSX language file for the given language.
 *
 * Bypasses the generated client and posts via `client.instance` directly, constructing the
 * multipart body manually — analog to `conversationApi.postMessage` — so the single "file"
 * part is built explicitly instead of relying on generated form-serialisation defaults.
 *
 * Returns the newly persisted import-protocol entry — HTTP 200 even when the file is
 * structurally rejected (`success=false`); only a transport-level failure (e.g. the
 * multipart size limit) rejects the returned promise.
 */
export async function importLanguage(language: Language, file: File): Promise<I18NImportLogDTO> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await importClient.instance.post<I18NImportLogDTO>(`/api/admin/i18n/${language}/import`, formData);
    return response.data;
}
